import React from 'react';
import FormNotice from './FormNotice';
import SubmitSection from './SubmitSection';
import CopyField from './CopyField';
import { PAYMENT_INFO, NOTICES, HOUSING_TYPES } from './constants';

const StepPaymentInfo = ({
  formData,
  kvkkAccepted,
  onKvkkChange,
  onKvkkModalOpen,
  onSubmit,
  isSubmitting,
  showAlert,
  isSuccess,
  result
}) => {
  const secilenTip = HOUSING_TYPES.find(tip => tip.kod === formData.konutTipi);
  const tipMetni = secilenTip
    ? `${secilenTip.baslik} — ${secilenTip.konsept} (${secilenTip.metrekare})`
    : '';

  if (isSuccess) {
    // Ödeme bilgileri ve açıklama sunucu tarafından üretilir; yanıt gelmezse sabitlere düşülür.
    const odeme = result?.odeme || PAYMENT_INFO;

    return (
      <div className="animate-fadeIn">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">
            Ön Başvurunuz Tamamlandı!
          </h2>
          <div className="bg-gray-100 h-px w-24 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium px-4 leading-relaxed">
            Ön başvuru kaydınız başarıyla oluşturulmuştur. Kaydınızın geçerli sayılması için son adım olarak aşağıdaki ön başvuru bedelini yatırmanız gerekmektedir.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            {result?.basvuruNo && (
              <span className="inline-block bg-primary-light text-primary-text text-sm font-semibold px-4 py-2 rounded-full">
                Başvuru No: {result.basvuruNo}
              </span>
            )}
            {(result?.konutTipi || formData.konutTipi) && (
              <span className="inline-block bg-primary-light text-primary-text text-sm font-semibold px-4 py-2 rounded-full">
                Konut Tipi: {result?.konutTipiMetni || tipMetni}
              </span>
            )}
          </div>
          <p className="mt-4 text-sm text-gray-500 px-4">
            Ön başvuru bilgileriniz SMS ile telefonunuza gönderilmiştir.
          </p>
        </div>

        {/* Payment Information - ONLY SHOWN AFTER SUCCESS */}
        <div className="bg-primary-light p-6 rounded-xl border border-primary/20 mb-6 shadow-sm">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-primary mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900">
              Ödeme Bilgileri ve IBAN
            </h3>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-primary/10">
              <span className="text-gray-700 font-medium">Banka:</span>
              <span className="text-sm font-bold text-gray-900">{odeme.banka || PAYMENT_INFO.bankName}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-primary/10">
              <span className="text-gray-700 font-medium">Ön Başvuru Bedeli:</span>
              <span className="text-2xl font-bold text-primary">
                {odeme.tutar || secilenTip?.onBasvuruBedeli || '-'}
              </span>
            </div>

            <CopyField
              label="Hesap Sahibi / Alıcı Adı"
              value={odeme.hesapSahibi || PAYMENT_INFO.accountHolder}
              successMessage="Hesap adı kopyalandı!"
              showAlert={showAlert}
              className="font-bold text-[10px] sm:text-xs tracking-tight text-center"
            />

            <CopyField
              label="IBAN Numarası"
              value={odeme.iban || PAYMENT_INFO.iban}
              copyValue={(odeme.iban || PAYMENT_INFO.iban).replace(/\s/g, '')}
              successMessage="IBAN kopyalandı!"
              showAlert={showAlert}
              className="font-mono text-sm tracking-tighter text-center"
            />

            <div className="bg-white p-4 rounded-lg border border-primary/20">
              <p className="text-sm text-gray-700 mb-1 font-medium">
                Ödeme Açıklaması
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Ön başvurunuza özel olarak otomatik üretilmiştir. Bankadaki açıklama alanına birebir bu metni yazınız.
              </p>
              <CopyField
                label=""
                value={odeme.aciklama || `${formData.firstName} ${formData.lastName} - ${formData.tcNo}`}
                successMessage="Açıklama kopyalandı!"
                showAlert={showAlert}
                className="font-mono text-xs sm:text-sm break-all"
              />
            </div>
          </div>
        </div>

        <FormNotice items={NOTICES.iban} variant="warning" />

        <button
          onClick={() => window.location.reload()}
          className="w-full mt-8 py-4 px-6 rounded-xl font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all border-2 border-transparent hover:border-gray-300"
        >
          Ana Sayfaya Dön
        </button>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
        Ön Başvuru Özeti ve Onay
      </h2>
      <p className="text-gray-600 mb-8">
        Lütfen bilgilerinizi kontrol ediniz ve ön başvurunuzu onaylayınız.
      </p>

      {/* Form Summary Review */}
      <div className="space-y-4 mb-6 bg-gray-50 p-6 rounded-2xl border border-gray-100">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Konut Tipi</p>
            <p className="text-gray-900 font-medium">{tipMetni || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Ön Başvuru Bedeli</p>
            <p className="text-gray-900 font-medium">{secilenTip?.onBasvuruBedeli || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Ad Soyad</p>
            <p className="text-gray-900 font-medium">{formData.firstName} {formData.lastName}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">T.C. Kimlik No</p>
            <p className="text-gray-900 font-medium">{formData.tcNo}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Telefon Numarası</p>
            <p className="text-gray-900 font-medium">{formData.phoneNumber}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">İl / İlçe</p>
            <p className="text-gray-900 font-medium">{[formData.il, formData.ilce].filter(Boolean).join(' / ')}</p>
          </div>
          <div className="col-span-2">
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Açık Adres</p>
            <p className="text-gray-900 font-medium leading-relaxed">{formData.address}</p>
          </div>
        </div>
      </div>

      {/* Bedel bilgilendirmesi */}
      <div className="mb-8 p-4 bg-primary-light border-l-4 border-primary/50 rounded-r-lg">
        <p className="text-sm text-primary-text leading-relaxed">
          <span className="font-bold">
            Şimdi ödenecek tutar — Ön Başvuru Bedeli: {secilenTip?.onBasvuruBedeli || '-'}
          </span>
          {secilenTip && (
            <span className="block mt-1">
              Peşinat ({secilenTip.pesinat}) ve aidat ({secilenTip.aidat}) bedelleri bu aşamada tahsil edilmez;
              kesin üyelik sonrasında ayrıca alınacaktır.
            </span>
          )}
          <span className="block mt-1">
            Ön başvurunuzu gönderdikten sonra ödeme yapacağınız IBAN ve size özel üretilmiş ödeme açıklaması ekranda gösterilecektir.
            Bedel yatırılmadan ön başvurunuz geçerli sayılmaz ve kuraya dâhil edilmez.
          </span>
        </p>
      </div>

      {/* KVKK and Submit Section */}
      <SubmitSection
        kvkkAccepted={kvkkAccepted}
        onKvkkChange={onKvkkChange}
        onKvkkModalOpen={onKvkkModalOpen}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default StepPaymentInfo;
